import {
  Area,
  ChartContainer,
  ChartGrid,
  ChartTooltip,
  ChartTooltipTimestampHeader,
  TimeXAxis,
  YAxis,
  Box,
} from '@cast/design-system';
import {
  clusterDashboardChartZoomProps,
  type SpendBreakdownChartPoint,
} from '@cast/features-cluster-shared';
import { dashesOrNum, formatCurrency, getCurrencyPrecision } from '@cast/utils';

import { clusterDashboardChartColors } from '../../_constants/dashboardTokens';

const formatCostValue = (value: number) =>
  formatCurrency(value, {
    maximumFractionDigits: getCurrencyPrecision(value),
  });

type MonthlyClusterCostChartProps = {
  data: SpendBreakdownChartPoint[];
  timeZone: string;
  isLoading?: boolean;
  hasNoData?: boolean;
  testId?: string;
};

export const MonthlyClusterCostChart = ({
  data,
  timeZone,
  isLoading,
  hasNoData,
  testId = 'monthly-cost-chart',
}: MonthlyClusterCostChartProps) => (
  <Box height={315} width="100%">
    <ChartContainer
      data={data}
      isLoading={isLoading}
      hasNoData={hasNoData}
      noDataOverlayProps={{ title: 'No cost data available' }}
      margin={{ left: 8, right: 8, top: 8, bottom: 0 }}
      fallbackChartType="area"
      testId={testId}
      zoomProps={clusterDashboardChartZoomProps}
      chartActionsSx={{ top: 0, right: 0 }}
      sx={{ height: '100%' }}
    >
      <Area
        dataKey="spot"
        fill={clusterDashboardChartColors.spot}
        fillOpacity={0.95}
        stackId="spend"
        stroke={clusterDashboardChartColors.spot}
        strokeWidth={1.5}
        tooltipConfig={{ displayName: 'Spot' }}
      />
      <ChartGrid hideVertical />
      <Area
        dataKey="fallback"
        fill={clusterDashboardChartColors.fallback}
        fillOpacity={0.95}
        stackId="spend"
        stroke={clusterDashboardChartColors.fallback}
        strokeWidth={1.5}
        tooltipConfig={{ displayName: 'Fallback' }}
      />
      <Area
        dataKey="onDemand"
        fill={clusterDashboardChartColors.onDemand}
        fillOpacity={0.95}
        stackId="spend"
        stroke={clusterDashboardChartColors.onDemand}
        strokeWidth={1.5}
        tooltipConfig={{ displayName: 'On-demand' }}
      />
      <YAxis
        type="number"
        padding={{ top: 16 }}
        unitFormatter={(value) => formatCostValue(Number(value))}
      />
      <TimeXAxis unixDataKey="chartTimestampUnix" timezone={timeZone} />
      <ChartTooltip<SpendBreakdownChartPoint>
        header={({ data: point, testId: tooltipTestId }) => (
          <ChartTooltipTimestampHeader
            data={point}
            testId={tooltipTestId}
            timezone={timeZone}
            hideTime
          />
        )}
        formatValue={(value) =>
          dashesOrNum({
            value,
            mapNumber: formatCostValue,
          })
        }
      />
    </ChartContainer>
  </Box>
);
